import React from 'react';
import { LabelType } from '../../../../../types/labels';
import { Label } from '../../../../Label';
import { Link } from '../../../../Link';
import { useDeckContentSwitching } from '../../DeckContentSwitchingContext';
import { MiniHeroImage } from '../MiniHeroImage';
import { ArrowRightIcon, CardProps, useResolvedLiveCardContent } from './LiveCard';
import type { LiveCardType } from '../../../../../types/decks';
import type { useActions } from '../../../../../hooks/useActions';

type ResolvedButton = NonNullable<LiveCardType['button']>;

export function LiveCardMiniHero({ card, actions }: CardProps): React.ReactNode {
  const { showNextProgram } = useDeckContentSwitching();
  const { shouldShowNextProgram, hasNextProgramContent } = useResolvedLiveCardContent(
    card,
    showNextProgram
  );

  const [isCardHovered, setIsCardHovered] = React.useState(false);

  return (
    <div key={card.id}>
      <Link destination={card.destination} action={card.action} onAction={actions}>
        <div
          onMouseEnter={() => setIsCardHovered(true)}
          onMouseLeave={() => setIsCardHovered(false)}
          onFocus={() => setIsCardHovered(true)}
          onBlur={() => setIsCardHovered(false)}
          className="relative hover:scale-s 2xl:hover:scale-xs transition-transform pb-1"
        >
          <div className="relative w-full overflow-hidden">
            <div
              className="transition-opacity duration-500 ease-in-out"
              style={{ opacity: shouldShowNextProgram ? 0 : 1 }}
            >
              <MiniHeroPanel
                mobileImageUrl={card.mobileImageUrl}
                tabletImageUrl={card.tabletImageUrl}
                desktopImageUrl={card.desktopImageUrl}
                title={card.title ?? ''}
                labels={card.labels ?? []}
                button={card.button ?? null}
                secondaryText={card.secondaryText ?? null}
                subTitle={card.subTitle ?? null}
                subTitleIcon={card.subTitleIcon ?? null}
                showSecondary
                actions={actions}
                isCardHovered={isCardHovered}
              />
            </div>
            {hasNextProgramContent ? (
              <div
                className="absolute inset-0 transition-opacity duration-500 ease-in-out"
                style={{ opacity: shouldShowNextProgram ? 1 : 0 }}
              >
                <MiniHeroPanel
                  mobileImageUrl={card.mobileNextProgramImageUrl}
                  tabletImageUrl={card.tabletNextProgramImageUrl}
                  desktopImageUrl={card.desktopNextProgramImageUrl}
                  title={card.nextProgramTitle ?? ''}
                  labels={card.nextProgramLabels ?? []}
                  button={card.nextProgramButton ?? null}
                  secondaryText={null}
                  subTitle={null}
                  subTitleIcon={null}
                  showSecondary={false}
                  actions={actions}
                  isCardHovered={isCardHovered}
                />
              </div>
            ) : null}
          </div>
        </div>
      </Link>
    </div>
  );
}

function MiniHeroPanel({
  mobileImageUrl,
  tabletImageUrl,
  desktopImageUrl,
  title,
  labels,
  button,
  secondaryText,
  subTitle,
  subTitleIcon,
  showSecondary,
  actions,
  isCardHovered,
}: {
  mobileImageUrl: string | null;
  tabletImageUrl: string | null;
  desktopImageUrl: string | null;
  title: string;
  labels: LabelType[];
  button: ResolvedButton | null;
  secondaryText: string | null;
  subTitle: string | null;
  subTitleIcon: string | null;
  showSecondary: boolean;
  actions?: ReturnType<typeof useActions>;
  isCardHovered: boolean;
}): React.ReactNode {
  const hasSubTitleRow = showSecondary && Boolean(subTitle);
  const titleClampClass = hasSubTitleRow ? 'line-clamp-1' : 'line-clamp-2';

  return (
    <div className="relative w-full overflow-hidden">
      <MiniHeroImage
        mobileImageUrl={mobileImageUrl}
        tabletImageUrl={tabletImageUrl}
        desktopImageUrl={desktopImageUrl}
        alt={title}
      />

      <div className="absolute inset-x-0 bottom-0 flex flex-col items-start gap-3 px-3 py-4">
        {showSecondary && secondaryText ? (
          <div className="text-marker-xs 1200:text-marker-l text-white">{secondaryText}</div>
        ) : (
          <div className="flex flex-row">
            {labels.map(label => (
              <Label key={label.text} label={label} />
            ))}
          </div>
        )}

        <div className={`text-heading-xs ${titleClampClass} text-white`}>{title}</div>

        {showSecondary && subTitle ? (
          <div className="text-neutral-400 text-marker-xs 1200:text-marker-l line-clamp-1 flex flex-row items-center">
            {subTitleIcon === 'arrow_right' && <ArrowRightIcon />}
            {subTitle}
          </div>
        ) : null}

        {button ? (
          <MiniHeroCtaButton button={button} actions={actions} isCardHovered={isCardHovered} />
        ) : null}
      </div>
    </div>
  );
}

function MiniHeroCtaButton({
  button,
  actions,
  isCardHovered,
}: {
  button: ResolvedButton;
  actions?: ReturnType<typeof useActions>;
  isCardHovered: boolean;
}): React.ReactNode {
  const useFocusedBackgroundColor = isCardHovered && Boolean(button.focusedBackgroundColor);
  const useFocusedBackgroundOpacity = isCardHovered && button.focusedBackgroundOpacityInPct != null;
  const useActiveImage = isCardHovered && Boolean(button.activeImageUrl);

  const backgroundColor = useFocusedBackgroundColor
    ? (button.focusedBackgroundColor ?? undefined)
    : (button.backgroundColor ?? undefined);

  const backgroundOpacity = useFocusedBackgroundOpacity
    ? (button.focusedBackgroundOpacityInPct ?? 100) / 100
    : (button.backgroundOpacityInPct ?? 100) / 100;

  const imageUrl = useActiveImage ? button.activeImageUrl : button.imageUrl;

  return (
    <button
      type="button"
      onClick={e => {
        const hasNoActionToDispatch = !button.action || !actions;
        if (hasNoActionToDispatch) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        actions(button.action);
      }}
      aria-label={button.title || ''}
      className="relative flex h-10 items-center justify-center overflow-hidden rounded-full px-2"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 z-[1] transition-colors"
        style={{
          background: backgroundColor,
          opacity: backgroundOpacity,
        }}
      />
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          aria-hidden="true"
          className="relative z-[1] h-full w-auto object-contain"
        />
      ) : null}
    </button>
  );
}
